import React from 'react';
import { UpOutlined, DownOutlined, StarOutlined, StarFilled } from "@ant-design/icons";
import { Car } from '../../types/home';
import { useNavigate } from 'react-router-dom';
import { toggleFavorite } from '../../utils/toggleFavorites';
import './ClassifiedCarCards.scss';

interface ClassifiedCarCardProps {
    car: Car;
    toggleStar?: (name: string) => void;
    onNavigateToDetails?: () => void;
}

const CarTags: React.FC<{ car: Car }> = ({ car }) => {
    // Utility functions
    const isNumeric = (value: any) => {
        if (typeof value === 'string') {
            value = value.replace(/[^0-9.-]+/g, ''); // Remove non-numeric characters
        }
        return !isNaN(parseFloat(value)) && isFinite(value);
    };

    const cleanNumericValue = (value: string) => parseFloat(value.replace(/[^0-9.-]+/g, ''));

    // Calculations
    const currentBidValue = isNumeric(car.currentBid) ? cleanNumericValue(car.currentBid) : null;
    const averagePriceValue = isNumeric(car.AveragePrice) ? cleanNumericValue(car.AveragePrice) : null;
    const isBidHigher = currentBidValue !== null && averagePriceValue !== null && currentBidValue > averagePriceValue;
    const isInvalid = currentBidValue === null || averagePriceValue === null;
    const difference = (averagePriceValue && currentBidValue) ? averagePriceValue - currentBidValue : null;
    const formattedDifference = difference !== null
        ? `$${difference.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : null;

    return (
        <div className="car-tags">
            <span className="car-tag current-bid">
                Asking Price: {car.currentBid}
            </span>
            {!isInvalid && (
                <span className={`car-tag average-price ${isBidHigher ? 'higher-bid' : 'lower-bid'}`}>
                    {isBidHigher ? <DownOutlined className='arrow-down' /> : <UpOutlined className='arrow-up' />}
                    Avg Retail: {car.AveragePrice}
                </span>
            )}
            {!isInvalid && (
                <span className={`car-tag difference ${isBidHigher ? 'higher-bid' : 'lower-bid'}`}>
                    Margin: {formattedDifference}
                </span>
            )}
        </div>
    );
};

const ClassifiedCarCard: React.FC<ClassifiedCarCardProps> = ({ car, toggleStar, onNavigateToDetails }) => {
    const navigate = useNavigate();

    const handleViewClick = (car: Car) => {
        if (onNavigateToDetails) {
            onNavigateToDetails();
        }
        const encodedKey = encodeURIComponent(car.key); // Encode the key
        navigate(`/car/${encodedKey}`, { state: { car: car } });
    };

    const handleToggleStar = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (toggleStar) {
            toggleStar(car.name);
        } else {
            // Fallback if no toggleStar prop is provided (e.g. in SimilarLiveCars)
            // We might want to handle local state or just call the utility
            toggleFavorite({ link: car.link, favorited: !car.Favorited });
            // Note: This won't update UI without parent state change, but it's a start
        }
    };

    return (
        <div className="car-card">
            <div className="image-container">
                <img src={car.image} alt={car.name} />
                <div className="classified-label">
                    Classifieds
                </div>
                {(car.heat_score !== undefined && !isNaN(car.heat_score)) && (
                    <div className="heat-score-container">
                        <div className="heat-score">
                            {car.heat_score === 0 && "Inventory Gap"}
                            {car.heat_score === 1 && "High Demand"}
                            {car.heat_score === 2 && "CRM Match"}
                            {car.heat_score === 3 && "Past Sales Match"}
                        </div>
                        <div className="heat-score-tooltip">
                            {car.heat_score === 0 &&
                                "This vehicle represents an inventory gap in your dealership. Adding this vehicle could help diversify your inventory offerings."}
                            {car.heat_score === 1 &&
                                "This vehicle is in high demand in your market area. Data shows strong consumer interest and potentially quick turnover for this model."}
                            {car.heat_score === 2 &&
                                "This vehicle matches interests from customers in your CRM. There are potential buyers already in your database who have shown interest in similar vehicles."}
                            {car.heat_score === 3 &&
                                "This vehicle is similar to models that have sold well at your dealership in the past. Historical sales data suggests this would be a good addition."}
                        </div>
                    </div>
                )}
            </div>
            <div className="car-details">
                <div className="name-row">
                    <h3 className="car-name">{car.name}</h3>
                    <span className="website-name">{car.website}</span>
                </div>
                <CarTags car={car} />
                <p className="car-location">Location: {car.location}</p>
                <div className="container">
                    <span
                        className="star-icon"
                        onClick={handleToggleStar}
                    >
                        {car.Favorited ? (
                            <StarFilled
                                style={{ color: "gold" }}
                                className="cart-icon active"
                            />
                        ) : (
                            <StarOutlined />
                        )}
                    </span>
                    <button
                        className="view-button"
                        onClick={() => handleViewClick(car)}
                    >
                        View Details
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClassifiedCarCard;
